import { useArtifactKinds } from "@/api/queries";
import { Field } from "@/components/ui/Field";
import { Select, Toggle } from "@/components/ui/Select";
import type { Draft } from "./presentation";

/**
 * How a result is drawn, as a form (TILE-1).
 *
 * The same vocabulary the model writes — every chart kind, the two maps, a
 * table — with only the fields the chosen kind reads, filled from the
 * result's own columns. Nothing here decides whether a choice fits the data:
 * the server materialises the preview and says so in the artifact's notes,
 * exactly as it does for an answer. Two implementations of "can this be a
 * line chart" would be one too many.
 */

function ColumnSelect({
  label,
  value,
  columns,
  onChange,
  optional = false,
  hint,
}: {
  label: string;
  value: string;
  columns: string[];
  onChange: (value: string) => void;
  optional?: boolean;
  hint?: string;
}) {
  return (
    <Select label={label} value={value} hint={hint} onChange={(event) => onChange(event.target.value)}>
      <option value="">{optional ? "None" : "Let it choose"}</option>
      {columns.map((column) => (
        <option key={column} value={column}>
          {column}
        </option>
      ))}
    </Select>
  );
}

export function PresentationFields({
  draft,
  columns,
  onChange,
}: {
  draft: Draft;
  columns: string[];
  onChange: (draft: Draft) => void;
}) {
  const { data: catalog } = useArtifactKinds();
  const kinds = catalog?.kinds ?? [];
  const family = kinds.find((kind) => kind.type === draft.type)?.family ?? null;
  const set = <K extends keyof Draft>(key: K) => (value: Draft[K]) => onChange({ ...draft, [key]: value });
  const share = draft.type === "pie_chart" || draft.type === "donut_chart";
  const axis = family === "categorical" || family === "series";

  return (
    <div className="flex flex-col gap-4">
      <Select label="Show as" value={draft.type} onChange={(event) => set("type")(event.target.value)}>
        <option value="table">Table</option>
        {(catalog?.families ?? []).map((group) => (
          <optgroup key={group} label={group[0]!.toUpperCase() + group.slice(1)}>
            {kinds
              .filter((kind) => kind.family === group)
              .map((kind) => (
                <option key={kind.type} value={kind.type}>
                  {kind.label}
                </option>
              ))}
          </optgroup>
        ))}
      </Select>

      {draft.type === "table" ? null : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {axis ? (
            <>
              <ColumnSelect label={family === "series" ? "Along" : "Category"} value={draft.x} columns={columns} onChange={set("x")} />
              <ColumnSelect label="Measure" value={draft.y} columns={columns} onChange={set("y")} />
              {family === "series" ? (
                <ColumnSelect label="Split by" value={draft.series_by} columns={columns} onChange={set("series_by")} optional />
              ) : null}
              {share ? null : (
                <ColumnSelect
                  label="Margin of error (±)"
                  value={draft.error}
                  columns={columns}
                  onChange={set("error")}
                  optional
                  hint="Drawn as whiskers on bars and a band on lines."
                />
              )}
            </>
          ) : null}

          {family === "correlation" ? (
            <>
              <ColumnSelect label="Across (x)" value={draft.x} columns={columns} onChange={set("x")} />
              <ColumnSelect label="Up (y)" value={draft.y} columns={columns} onChange={set("y")} />
              <ColumnSelect label="Point label" value={draft.label} columns={columns} onChange={set("label")} optional />
              {draft.type === "bubble_chart" ? (
                <ColumnSelect label="Bubble size" value={draft.size} columns={columns} onChange={set("size")} />
              ) : null}
              <Toggle
                label="Trend line"
                hint="Least squares, with r and n. Association, not cause."
                checked={draft.trendline}
                onChange={set("trendline")}
              />
              <Field
                label="Name the outliers"
                type="number"
                min={0}
                max={10}
                value={draft.label_outliers}
                onChange={(event) => set("label_outliers")(event.target.value)}
                hint="How many of the points furthest from the line to label."
              />
            </>
          ) : null}

          {family === "distribution" ? (
            <ColumnSelect label="Measure" value={draft.x} columns={columns} onChange={set("x")} />
          ) : null}

          {family === "matrix" ? (
            <>
              <ColumnSelect label="Columns" value={draft.x} columns={columns} onChange={set("x")} />
              <ColumnSelect label="Rows" value={draft.series_by} columns={columns} onChange={set("series_by")} />
              <ColumnSelect label="Measure" value={draft.y} columns={columns} onChange={set("y")} />
            </>
          ) : null}

          {family === "geographic" ? (
            <>
              <ColumnSelect
                label="Places"
                value={draft.location}
                columns={columns}
                onChange={set("location")}
                hint="State codes or names, county FIPS, or countries."
              />
              <Select label="Boundaries" value={draft.geo} onChange={(event) => set("geo")(event.target.value)}>
                <option value="">Work it out from the places</option>
                <option value="us_states">US states</option>
                <option value="us_counties">US counties</option>
                <option value="world_countries">World countries</option>
              </Select>
              {draft.type === "bubble_map" ? (
                <>
                  <ColumnSelect label="Bubble size" value={draft.size} columns={columns} onChange={set("size")} />
                  <ColumnSelect label="Latitude" value={draft.lat} columns={columns} onChange={set("lat")} optional hint="Instead of places, for points of your own." />
                  <ColumnSelect label="Longitude" value={draft.lon} columns={columns} onChange={set("lon")} optional />
                </>
              ) : (
                <ColumnSelect label="Shade by" value={draft.y} columns={columns} onChange={set("y")} />
              )}
            </>
          ) : null}

          {family && family !== "distribution" && family !== "matrix" ? (
            <>
              <Field
                label="Highlight"
                value={draft.highlight}
                onChange={(event) => set("highlight")(event.target.value)}
                placeholder="Texas, Florida, Georgia"
                hint="Comma-separated; everything else is muted."
              />
              <ColumnSelect
                label="Switch between"
                value={draft.control}
                columns={columns}
                onChange={set("control")}
                optional
                hint="A control above the chart — one view per value, on one shared scale."
              />
            </>
          ) : null}

          {axis && !share ? (
            <>
              <Field label="Shade from" value={draft.band_from} onChange={(event) => set("band_from")(event.target.value)} placeholder="2023-04" />
              <Field label="Shade to" value={draft.band_to} onChange={(event) => set("band_to")(event.target.value)} placeholder="2024-06" />
              <Field label="Shaded band's label" value={draft.band_label} onChange={(event) => set("band_label")(event.target.value)} placeholder="Unwinding" />
              <Field label="Reference line at" value={draft.line_at} onChange={(event) => set("line_at")(event.target.value)} placeholder="100" inputMode="decimal" />
              <Field label="Reference line's label" value={draft.line_label} onChange={(event) => set("line_label")(event.target.value)} placeholder="February 2020 = 100" />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
